import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';

const Home = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.6 }
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1598371839696-5c5bb00bdc28?auto=format&fit=crop&q=80&w=2000" 
            alt="Tattoo Artist Working" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background"></div>
        </div>
        
        <div className="container relative z-10 mx-auto px-6 text-center mt-20">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-light mb-6 leading-tight"
          >
            Custom Tattoos <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-light">Crafted With Precision.</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-lg md:text-xl text-muted max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Montreal tattoo studio specializing in custom artwork, blackwork, realism, fine line, and unique designs created around your story.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/booking" className="w-full sm:w-auto bg-secondary text-primary px-8 py-4 font-medium hover:bg-light transition-colors text-center uppercase tracking-wider text-sm">
              Book Consultation
            </Link>
            <Link to="/portfolio" className="w-full sm:w-auto border border-gray-600 text-light px-8 py-4 font-medium hover:border-secondary hover:text-secondary transition-colors text-center uppercase tracking-wider text-sm">
              View Portfolio
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Artists */}
      <section className="py-24 bg-primary">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display mb-4">Meet The Artists</h2>
            <div className="w-20 h-1 bg-secondary mx-auto mb-6"></div>
            <p className="text-muted max-w-2xl mx-auto">Award-winning professionals dedicated to turning your vision into a lifelong masterpiece.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: 'Alex Mercer', role: 'Blackwork Specialist', img: 'https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?auto=format&fit=crop&q=80&w=600' },
              { name: 'Sofia Reyes', role: 'Fine Line Artist', img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=600' },
              { name: 'Jordan Blake', role: 'Realism Artist', img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=600' },
              { name: 'Maya Stone', role: 'Traditional Specialist', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=600' }
            ].map((artist, idx) => (
              <motion.div 
                key={artist.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group relative overflow-hidden cursor-pointer"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={artist.img} alt={artist.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-transparent opacity-90"></div>
                <div className="absolute bottom-0 left-0 w-full p-6 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-xl font-display text-light">{artist.name}</h3>
                  <p className="text-secondary text-sm mb-4">{artist.role}</p>
                  <Link to="/artists" className="text-light text-sm flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    View Profile <SafeIcon icon={FiArrowRight} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <motion.div {...fadeIn} className="lg:w-1/2">
              <img src="https://images.unsplash.com/photo-1595747644932-abb68f85f419?auto=format&fit=crop&q=80&w=800" alt="Studio details" className="w-full h-[600px] object-cover rounded-sm grayscale hover:grayscale-0 transition-all duration-700" />
            </motion.div>
            
            <motion.div {...fadeIn} className="lg:w-1/2 space-y-8">
              <div>
                <h2 className="text-3xl md:text-5xl font-display mb-6">A Premium Studio Experience</h2>
                <p className="text-muted leading-relaxed">
                  Located in the heart of downtown Montreal on Saint Catherine Street, Black Anchor is designed to provide a comfortable, professional, and immaculate environment for your tattoo journey.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { title: 'Impeccable Hygiene Standards', desc: 'Hospital-grade sterilization, single-use needles, and strict cross-contamination protocols.' },
                  { title: 'Custom Original Artwork', desc: 'We never copy. Every piece is custom-designed for your body and your story.' },
                  { title: 'Comfortable Environment', desc: 'A welcoming, judgment-free space with private rooms available upon request.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1">
                      <SafeIcon icon={FiCheckCircle} className="text-secondary text-xl" />
                    </div>
                    <div>
                      <h4 className="text-light font-medium text-lg mb-1">{item.title}</h4>
                      <p className="text-muted text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="pt-6">
                <Link to="/styles" className="inline-flex items-center gap-2 text-secondary hover:text-light transition-colors uppercase tracking-wider text-sm font-medium">
                  Explore Our Styles <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-primary border-t border-gray-900">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display mb-4">Client Stories</h2>
            <div className="w-20 h-1 bg-secondary mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Marc-Antoine D.", text: "Alex did an incredible half-sleeve blackwork piece for me. The attention to detail and the sheer professionalism of the studio blew me away. Best in Montreal." },
              { name: "Sarah Jenkins", text: "Got my first fine line tattoo with Sofia. She was so patient, explained the whole process, and the result is more delicate and beautiful than I imagined." },
              { name: "David L.", text: "The hygiene standards here are top-notch. You walk in and immediately feel you're in good hands. Jordan's realism work healed perfectly." }
            ].map((review, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
                className="bg-background p-8 border border-gray-800 hover:border-secondary/50 transition-colors"
              >
                <div className="flex text-secondary mb-4">
                  {[1,2,3,4,5].map(star => <span key={star}>★</span>)}
                </div>
                <p className="text-muted italic mb-6 leading-relaxed">"{review.text}"</p>
                <p className="text-light font-display">{review.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;